import Button from 'components/Button';
import Error from 'components/form/Error';
import { FormRadio, FormRadioGroup } from 'components/form/FormRadioGroup';
import { FlexColumn, Grid } from 'components/form/generic';
import Input from 'components/form/Inputs/Input';
import LabelledCheckbox from 'components/form/Inputs/LabelledCheckbox';
import Radio from 'components/form/Inputs/Radio';
import TextAreaDisplay from 'components/form/Inputs/TextAreaDisplay';
import Field from 'components/profileNavigation/Field';
import CONSIDERATIONS from 'enums/Considerations';
import EXPERTISE from 'enums/Expertise';
import FORMATIONS from 'enums/Formation';
import HOBBIES from 'enums/Hobby';
import MENTOR_HEARD_ABOUT_MENTORSHIP from 'enums/MentorHeardAboutMentorship';
import { MENTOR_PRIORITY_LEVELS } from 'enums/PriorityLevels';
import STUDY_LEVELS from 'enums/StudyLevels';
import { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import MentorForm from 'types/MentorForm';
import privateAPI from 'utils/privateAxiosInstance';
import { PermissionsContainer } from './components';

const WantedMentorshipContainer = styled.div`
  display: flex;
  max-width: 40rem;
  column-gap: 1.5rem;
  & > * {
    /* margin-right: 1.5rem; */
    width: 100%;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  button {
    flex-grow: 1;
  }
  button:first-child {
    margin-right: 1rem;
  }
`;

type Props = {
  mentorForm: MentorForm | undefined;
  reader?: 'default' | 'admin';
};
const MentorFormDisplay: FC<Props> = ({ mentorForm, reader }) => {
  const [editingWanted, setEditingWanted] = useState<boolean>(false);

  const [wantedMentorships, setWantedMentorships] = useState<string>(
    mentorForm?.wantedMentorships?.toString() || '',
  );
  useEffect(() => {
    mentorForm &&
      setWantedMentorships(mentorForm.wantedMentorships?.toString() || '');
  }, [mentorForm, setWantedMentorships]);
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setWantedMentorships(e.target.value);
  };

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const cancelEditing = useCallback(() => {
    setEditingWanted(false);
    mentorForm &&
      setWantedMentorships(mentorForm.wantedMentorships?.toString() || '');
    setError(false);
  }, [mentorForm, setWantedMentorships, setEditingWanted, setError]);

  const handleSave = () => {
    setLoading(true);
    setError(false);
    privateAPI
      .post(`/mentor-form/wanted-mentorships/${mentorForm?.id}`, {
        wantedMentorships: parseInt(wantedMentorships),
      })
      .then(() => {
        setEditingWanted(false);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Field label="Appuis apportés">
        <FlexColumn>
          <FormRadioGroup
            title="L'amélioration des techniques de recherche d'emploi"
            name="job_search"
            value={mentorForm?.jobSearch || null}
            readOnly
          >
            <FormRadio
              label={MENTOR_PRIORITY_LEVELS['PRIORITY']}
              id="PRIORITY"
            />
            <FormRadio label={MENTOR_PRIORITY_LEVELS['YES']} id="YES" />
            <FormRadio label={MENTOR_PRIORITY_LEVELS['NO']} id="NO" />
          </FormRadioGroup>
          <FormRadioGroup
            title="Définition du projet de formation ou projet professionnel"
            name="career_plan"
            value={mentorForm?.careerPlan || null}
            readOnly
          >
            <FormRadio
              label={MENTOR_PRIORITY_LEVELS['PRIORITY']}
              id="PRIORITY"
            />
            <FormRadio label={MENTOR_PRIORITY_LEVELS['YES']} id="YES" />
            <FormRadio label={MENTOR_PRIORITY_LEVELS['NO']} id="NO" />
          </FormRadioGroup>
          <FormRadioGroup
            title="Conseils pour réussir dans ses études"
            name="studies"
            value={mentorForm?.studies || null}
            readOnly
          >
            <FormRadio
              label={MENTOR_PRIORITY_LEVELS['PRIORITY']}
              id="PRIORITY"
            />
            <FormRadio label={MENTOR_PRIORITY_LEVELS['YES']} id="YES" />
            <FormRadio label={MENTOR_PRIORITY_LEVELS['NO']} id="NO" />
          </FormRadioGroup>
          <FormRadioGroup
            title="Création ou développement d'une activité"
            name="entrepreneurship"
            value={mentorForm?.entrepreneurship || null}
            readOnly
          >
            <FormRadio
              label={MENTOR_PRIORITY_LEVELS['PRIORITY']}
              id="PRIORITY"
            />
            <FormRadio label={MENTOR_PRIORITY_LEVELS['YES']} id="YES" />
            <FormRadio label={MENTOR_PRIORITY_LEVELS['NO']} id="NO" />
          </FormRadioGroup>
        </FlexColumn>
      </Field>

      <Field label="Domaines d'apports de compétences ou réseau">
        <Grid>
          {mentorForm?.expertiseFields?.map((expertiseField) => (
            <LabelledCheckbox
              key={expertiseField.id}
              readOnly
              checked
              label={EXPERTISE[expertiseField.expertise]}
            />
          ))}
        </Grid>
      </Field>

      <Field label="Parcours de formation">
        <Grid>
          {mentorForm?.formations?.map((mentorFormation) => (
            <LabelledCheckbox
              key={mentorFormation.id}
              readOnly
              checked
              label={FORMATIONS[mentorFormation.formation]}
            />
          ))}
        </Grid>
      </Field>
      <Field label="Plus à l'aise avec les niveaux de diplômes">
        <Grid>
          {mentorForm?.diploma?.map((mentorDiploma, idx) => (
            <LabelledCheckbox
              key={idx}
              readOnly
              checked
              label={STUDY_LEVELS[mentorDiploma.diploma]}
            />
          ))}
        </Grid>
      </Field>

      <Field label="Rural/Urbain">
        <Radio
          readOnly
          checked={!!mentorForm?.consideration}
          label={
            mentorForm?.consideration
              ? CONSIDERATIONS[mentorForm.consideration]
              : 'Non précisé'
          }
        />
      </Field>

      <Field label="Temps libre">
        <Radio
          readOnly
          checked={!!mentorForm?.hobby}
          label={mentorForm?.hobby ? HOBBIES[mentorForm.hobby] : 'Non précisé'}
        />
      </Field>
      <Field label="Nombre de mentorés désirés">
        <FlexColumn gap={0.5}>
          {error && <Error>Une erreur est survenue.</Error>}
          <WantedMentorshipContainer>
            <Input
              type="number"
              readOnly={!editingWanted}
              value={wantedMentorships}
              onChange={handleChange}
            />
            {reader === 'admin' &&
              (editingWanted ? (
                <ButtonsContainer>
                  <Button variant="outlined" onClick={cancelEditing}>
                    Annuler
                  </Button>
                  <Button
                    color="primary"
                    loading={loading}
                    onClick={handleSave}
                  >
                    Enregistrer
                  </Button>
                </ButtonsContainer>
              ) : (
                <Button color="primary" onClick={() => setEditingWanted(true)}>
                  Modifier
                </Button>
              ))}
          </WantedMentorshipContainer>
        </FlexColumn>
      </Field>

      <Field label="Comment avez-vous entendu parler du mentorat ?">
        {mentorForm?.heardAboutMentorship === 'OTHER'
          ? mentorForm.heardAboutMentorshipText || 'Autre (non précisé)'
          : mentorForm?.heardAboutMentorship &&
            MENTOR_HEARD_ABOUT_MENTORSHIP[mentorForm.heardAboutMentorship]}
      </Field>

      <Field label="Ce que j'attends du mentorat">
        <TextAreaDisplay text={mentorForm?.expectations || ''} />
      </Field>

      <Field label="Partage des informations avec le mentoré">
        <PermissionsContainer>
          <LabelledCheckbox
            label="J’autorise le partage de mon adresse email avec le mentoré"
            checked={!!mentorForm?.sharingEmail}
            readOnly
          />
          <LabelledCheckbox
            label="J’autorise le partage de mon numéro de téléphone avec le mentoré"
            checked={!!mentorForm?.sharingPhone}
            readOnly
          />
          <div className="or">OU</div>
          <LabelledCheckbox
            label="Je n’autorise pas le partage de mes informations avec le mentoré"
            checked={
              mentorForm?.sharingEmail === false &&
              mentorForm?.sharingPhone === false
            }
            readOnly
          />
        </PermissionsContainer>
      </Field>
    </>
  );
};

export default MentorFormDisplay;
